<template>
  <div class="ident-shell">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-container fluid class="pa-5">
      <!-- ============ hero ============ -->
      <div class="ident-hero">
        <span class="hero-rail"></span>
        <div class="hero-left">
          <div class="hero-badge"><v-icon size="25">mdi-help-circle-outline</v-icon></div>
          <div class="hero-copy">
            <div class="hero-eyebrow">
              <v-icon size="13">mdi-image-search-outline</v-icon>
              Identification Request
            </div>
            <div class="hero-title">{{ reporterName }}</div>
            <div class="hero-meta">
              <span class="meta-chip" v-if="incidentDate">
                <v-icon size="13">mdi-calendar-clock</v-icon>
                {{ incidentDate }}
              </span>
              <span class="meta-chip" v-if="reporterPhone">
                <v-icon size="13">mdi-phone-outline</v-icon>
                {{ reporterPhone }}
              </span>
              <span class="meta-chip meta-chip--warn" v-if="data.issnakebite">
                <v-icon size="13">mdi-alert-outline</v-icon>
                Reported as a snake bite
              </span>
            </div>
          </div>
        </div>
        <div class="hero-right">
          <span :class="['status-chip', data.didadminreply ? 'status-done' : 'status-open']">
            <span class="status-dot"></span>
            {{ data.didadminreply ? "Replied" : "Awaiting reply" }}
          </span>
        </div>
      </div>

      <v-layout wrap justify-start>
        <!-- ============ what was submitted ============ -->
        <v-flex xs12 lg5 pa-2>
          <div class="panel fill">
            <div class="panel-head">
              <div class="panel-icon"><v-icon size="19">mdi-camera-outline</v-icon></div>
              <div class="panel-copy">
                <div class="cardtitle">Submitted Photo</div>
                <div class="subtext">What the reporter sent in for identification</div>
              </div>
            </div>

            <div class="photo-frame" v-if="data.photo">
              <img :src="mediaURL + data.photo" class="photo" alt="Submitted snake photo" />
            </div>
            <div class="empty-block" v-else>
              <v-icon size="30">mdi-image-off-outline</v-icon>
              <span>No photo was attached</span>
            </div>

            <div class="note-block" v-if="data.description">
              <div class="note-head">
                <v-icon size="15">mdi-text-account</v-icon>
                <span class="itemKey">Reporter's Note</span>
              </div>
              <div class="note-body">{{ data.description }}</div>
            </div>
            <div class="flag-line" v-if="data.hasPhotoIssue">
              <v-icon size="15">mdi-alert-circle-outline</v-icon>
              This photo has been flagged as unclear
            </div>
          </div>
        </v-flex>

        <!-- ============ location ============ -->
        <v-flex xs12 lg7 pa-2>
          <div class="panel fill">
            <div class="panel-head">
              <div class="panel-icon"><v-icon size="19">mdi-map-marker-radius-outline</v-icon></div>
              <div class="panel-copy">
                <div class="cardtitle">Location</div>
                <div class="subtext">Where the sighting was reported from</div>
              </div>
            </div>
            <div id="map" v-show="hasLocation"></div>
            <div class="empty-block" v-if="!hasLocation">
              <v-icon size="30">mdi-map-marker-off-outline</v-icon>
              <span>No location was recorded</span>
            </div>
          </div>
        </v-flex>
      </v-layout>

      <!-- ============ the reply ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon">
            <v-icon size="19">{{ data.didadminreply ? "mdi-check-decagram-outline" : "mdi-reply-outline" }}</v-icon>
          </div>
          <div class="panel-copy">
            <div class="cardtitle">{{ data.didadminreply ? "Identification Given" : "Reply Now" }}</div>
            <div class="subtext">
              {{
                data.didadminreply
                  ? "The identification already sent back to the reporter"
                  : "Identify the species and send the reporter an answer"
              }}
            </div>
          </div>
        </div>

        <!--
          The recorded answer used to vanish once didadminreply flipped: the form
          hid itself and nothing took its place, so there was no way to see what
          had been sent. Show it back.
        -->
        <v-layout wrap justify-start v-if="data.didadminreply">
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in replyFields" :key="i">
            <div class="detail-cell">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value }}</div>
              </div>
            </div>
          </v-flex>
          <v-flex xs12 pa-2 v-if="data.reply_details">
            <div class="note-block">
              <div class="note-head">
                <v-icon size="15">mdi-note-text-outline</v-icon>
                <span class="itemKey">Details Sent</span>
              </div>
              <div class="note-body">{{ data.reply_details }}</div>
            </div>
          </v-flex>
        </v-layout>

        <v-layout wrap justify-start v-else>
          <v-flex xs12 md6 pa-2>
            <div class="itemKey pb-1">Snake Name</div>
            <v-autocomplete
              v-model="name"
              :items="snakelist"
              outlined
              dense
              hide-details
              item-text="name"
              item-value="_id"
              placeholder="Select the identified species"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 md6 pa-2>
            <div class="itemKey pb-1">Venom Type</div>
            <v-select
              v-model="venomType"
              :items="venomtypes"
              outlined
              dense
              hide-details
              placeholder="Select venom type"
            ></v-select>
          </v-flex>
          <v-flex xs12 md6 pa-2>
            <div class="itemKey pb-1">Reply</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="reply"
              placeholder="Short answer to the reporter"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 md6 pa-2>
            <div class="itemKey pb-1">Description</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="description"
              placeholder="Any further detail worth passing on"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 pa-2 pt-4>
            <v-btn depressed class="btn-solid" @click="replyNow()">
              <v-icon small left>mdi-send-outline</v-icon>
              <span class="btnlabel">Send Reply</span>
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
    </v-container>
  </div>
</template>

<script src="https://maps.googleapis.com/maps/api/js?libraries=places&key=AIzaSyBpukNl1L1qGqEwad87A1-MkZj8H_bKDZM&libraries=visualization,drawing"></script>
<script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      data: {},
      map: "",
      location: [],
      snakelist: [],
      venomtypes: ["Highly Venomous", "Mildly Venomous", "Non Venomous"],
      venomType: "",
      reply: "",
      name: "",
      description: "",
    };
  },
  beforeMount() {
    this.getData();
    this.getList();
  },
  computed: {
    hasLocation() {
      return this.location && this.location.length === 2;
    },
    reporterName() {
      var d = this.data || {};
      if (d.added_by && (d.added_by.name || d.added_by.username)) {
        return d.added_by.name || d.added_by.username;
      }
      return "Identification Request";
    },
    reporterPhone() {
      var d = this.data || {};
      return d.added_by ? d.added_by.phone : null;
    },
    incidentDate() {
      return this.formatDate(this.data.timeofincident || this.data.createddate);
    },
    //the answer that was sent back, shown once the request has been replied to
    replyFields() {
      var d = this.data || {};
      var species =
        d.snake && d.snake.name ? d.snake.name : d.reply_name || null;
      var repliedBy =
        d.repliedadmin && (d.repliedadmin.name || d.repliedadmin.username)
          ? d.repliedadmin.name || d.repliedadmin.username
          : null;

      var rows = [
        { label: "Species", value: species, icon: "mdi-snake" },
        { label: "Venom Type", value: d.reply_venomtype, icon: "mdi-flask-outline" },
        { label: "Reply", value: d.reply, icon: "mdi-message-reply-text-outline" },
        { label: "Replied By", value: repliedBy, icon: "mdi-account-check-outline" },
        { label: "Expert Remark", value: d.expertremark, icon: "mdi-comment-quote-outline" },
      ];
      return rows.filter(function (r) {
        return r.value;
      });
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return null;
      var d = new Date(value);
      if (isNaN(d.getTime())) return null;
      var pad = function (n) {
        return n < 10 ? "0" + n : "" + n;
      };
      var h = d.getHours();
      //midnight and noon both read as 12 on a 12-hour clock, not 0
      var hour12 = h % 12 === 0 ? 12 : h % 12;
      return (
        pad(d.getDate()) +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        d.getFullYear() +
        " " +
        pad(hour12) +
        ":" +
        pad(d.getMinutes()) +
        " " +
        (h < 12 ? "AM" : "PM")
      );
    },

    initMap() {
      var vm = this;
      var mapCanvas = document.getElementById("map");
      if (!mapCanvas) return;

      // Set default map options
      var mapOptions = {
        zoom: 10,
        mapTypeId: google.maps.MapTypeId.TERRAIN,
      };
      vm.map = new google.maps.Map(mapCanvas, mapOptions);

      if (vm.location && vm.location.length == 2) {
        // Create a shallow copy of the location to avoid reactive object issues
        const locationCopy = JSON.parse(JSON.stringify(vm.location));

        var reportedLat = locationCopy[0]; // Latitude of reported location
        var reportedLon = locationCopy[1]; // Longitude of reported location

        mapOptions.center = new google.maps.LatLng(reportedLat, reportedLon);
        vm.map.setOptions(mapOptions);

        // Create a marker for the reported location
        var reportedMarker = new google.maps.Marker({
          map: vm.map,
          position: new google.maps.LatLng(reportedLat, reportedLon),
          title: "Reported Location",
        });

        // Create an InfoWindow for the reported location
        var reportedInfoWindow = new google.maps.InfoWindow({
          content: "Reported Location",
        });

        // Add a click event listener to the reported marker
        reportedMarker.addListener("click", function () {
          reportedInfoWindow.open(vm.map, reportedMarker);
        });
      }
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/adminreply/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.data = response.data.data;
            this.location = this.data.where;
            this.appLoading = false;
            this.msg = response.data.msg;
            this.showSnackBar = false;
            //the map div is behind v-show, so it exists either way; still wait a
            //tick so it has been laid out before Google measures it
            this.$nextTick(() => {
              this.initMap();
            });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/snake/all/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.snakelist = response.data.data;
            this.appLoading = false;
            this.msg = response.data.msg;
            this.showSnackBar = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          (this.ServerError = true), console.log(err);
        });
    },
    replyNow() {
      if (!this.name) {
        this.msg = "Please Provide Name.";
        this.showSnackBar = true;
        return;
      }
      if (!this.venomType) {
        this.msg = "Please Provide Venom Type.";
        this.showSnackBar = true;
        return;
      }
      if (!this.reply) {
        this.msg = "Please Provide Reply Message.";
        this.showSnackBar = true;
        return;
      }
      if (!this.description) {
        this.msg = "Please Provide description.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/adminreply/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          snake: this.name,
          venomtype: this.venomType,
          reply: this.reply,
          details: this.description,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Replied Successfully";
            this.showSnackBar = true;
            this.appLoading = false;
            setTimeout(() => {
              this.$router.push({ name: "identificationhelp" });
            }, 2000);
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
/* ================= design tokens =================
   The same emerald system as the dashboards and the alert view, so this page
   reads as part of the portal. */
.ident-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  min-height: 100vh;
  padding-bottom: 2rem;
}

/* ================= typography ================= */
.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}
.itemKey {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
.itemValue {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1a2e23;
  line-height: 1.45;
  word-break: break-word;
}
.btnlabel {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

/* ================= hero ================= */
.ident-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px 22px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, #f7fdfa 55%, #ecfdf5 100%);
  box-shadow: var(--shadow);
}
.hero-rail {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.hero-badge {
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* a step below the brand emerald so the white glyph clears 3:1 */
  background: linear-gradient(135deg, #0ea97a 0%, var(--ink) 100%);
  box-shadow: 0 8px 20px -8px rgba(5, 150, 105, 0.55);
}
.hero-badge .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  min-width: 0;
}
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-eyebrow .v-icon {
  color: var(--accent) !important;
}
.hero-title {
  margin-top: 3px;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 23px;
  line-height: 1.2;
  letter-spacing: -0.5px;
  color: #1a2e23;
  word-break: break-word;
}
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 11px;
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid var(--tint-line);
  border-radius: 999px;
  background: #ffffff;
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: #3f5c4e;
}
.meta-chip .v-icon {
  color: var(--accent) !important;
}
/* a reported bite is the one thing on this page that changes the urgency */
.meta-chip--warn {
  border-color: #f4d3db;
  background: #fff5f6;
  color: #9f1239;
}
.meta-chip--warn .v-icon {
  color: #be123c !important;
}
.hero-right {
  display: flex;
  align-items: center;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 15px;
  border-radius: 999px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.status-open {
  border: 1px solid #f2ddba;
  background: #fff9ef;
  color: #b45309;
}
.status-done {
  border: 1px solid var(--tint-line);
  background: var(--badge);
  color: var(--ink);
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
/* the photo and the map sit side by side; keep the two cards the same height */
.panel.fill {
  height: 100%;
  margin-top: 0;
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

/* ================= photo ================= */
.photo-frame {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: #f6f8fb;
}
.photo {
  display: block;
  width: 100%;
  max-height: 340px;
  object-fit: contain;
}
.empty-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 180px;
  border: 1px dashed var(--line);
  border-radius: 13px;
  background: #fcfefd;
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: #a7b6c2;
}
.empty-block .v-icon {
  color: #cbd5e1 !important;
}
.flag-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid #f2ddba;
  border-radius: 11px;
  background: #fff9ef;
  font-family: poppinsregular, sans-serif;
  font-size: 12px;
  color: #b45309;
}
.flag-line .v-icon {
  color: #d97706 !important;
}

/* ================= map ================= */
#map {
  height: 340px;
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 13px;
}

/* ================= detail cells ================= */
.detail-cell {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  height: 100%;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fcfefd;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.detail-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.detail-icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--tint-line);
  background: #ffffff;
}
.detail-icon .v-icon {
  color: var(--ink) !important;
}
.detail-copy {
  min-width: 0;
  padding-top: 1px;
}
.detail-copy .itemValue {
  margin-top: 3px;
}

/* ================= notes ================= */
.note-block {
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--accent);
  border-radius: 0 12px 12px 0;
  background: var(--tint);
}
.note-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.note-head .v-icon {
  color: var(--ink) !important;
}
.note-body {
  margin-top: 7px;
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: #3f5c4e;
  word-break: break-word;
}

/* ================= button ================= */
.btn-solid.v-btn {
  border-radius: 20px !important;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(5, 150, 105, 0.45) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: filter 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.btn-solid.v-btn,
.btn-solid.v-btn .btnlabel,
.btn-solid.v-btn .v-icon {
  color: #ffffff !important;
}
.btn-solid.v-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px -4px rgba(5, 150, 105, 0.5) !important;
}

@media (max-width: 700px) {
  .ident-hero {
    padding: 18px 18px 18px 22px;
  }
  .hero-title {
    font-size: 20px;
  }
  #map,
  .photo {
    height: 260px;
  }
}
</style>
